import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocalPharmacy as LocalPharmacyIcon,
  MedicalServices as MedicalServicesIcon,
  Restaurant as RestaurantIcon,
  EventNote as EventNoteIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowChip } from "./_rowParts";
import ConfirmDialog from "../../common/ConfirmDialog";
import TreatmentEditor from "./TreatmentEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import {
  useListTreatmentsByPatientMutation,
  useDeleteTreatmentMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const TAB_KEY = "treatments";

const STATUS_LABELS = {
  active: "Aktif",
  completed: "Tamamlandı",
  abandoned: "Bırakıldı",
  cancelled: "İptal Edildi",
};

const getStatusLabel = (status, isActive) => {
  if (isActive) return STATUS_LABELS.active;
  const key = String(status || "").toLowerCase();
  return STATUS_LABELS[key] || "Bilinmiyor";
};

const getStatusStyle = (status, isActive, palette) => {
  if (isActive || status === "active") {
    return { bgcolor: alpha(palette.primary, 0.08), color: palette.primary };
  }
  if (status === "completed") {
    return { bgcolor: COLORS.successLight, color: COLORS.successDarker };
  }
  if (status === "abandoned") {
    return { bgcolor: COLORS.warningLight, color: COLORS.warningDarker };
  }
  if (status === "cancelled") {
    return { bgcolor: COLORS.errorLight, color: COLORS.errorDark };
  }
  return { bgcolor: COLORS.slate200, color: COLORS.slate600 };
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
};

/**
 * TreatmentsTab — master-detail inline edit for treatment plans.
 *
 * Left panel: sorted treatment list. Right panel: TreatmentEditor backed by
 * useInlineAutosave. Selection is restored from localStorage in session mode.
 *
 * @property {"standalone" | "session"} mode
 * @property {string} patientId
 * @property {string} token
 * @property {boolean} [canEdit=true]
 * @property {object} [initialData]  Session: { treatments, diagnoses, complaints, medicineTemplates, foods, examinationId }
 * @property {(promise: Promise) => Promise} [registerAutoSave]
 * @property {(flush: () => Promise) => void} [registerFlush]
 * @property {(status: "idle"|"saving"|"saved"|"error") => void} [onSaveStatusChange]
 * @property {() => void} [onRecordChanged]
 * @property {string} [initialSelectedId] - açılışta seçilecek kayıt (klinik geneli listeden gelince)
 */
export default function TreatmentsTab({
  mode,
  patient,
  patientId,
  token,
  canEdit = true,
  initialData,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onRecordChanged,
  initialSelectedId,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // ── Local (section-internal) alert ─────────────────────────────
  const [localAlert, setLocalAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback(
    (message, severity = "success", duration = 3500) => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
      setLocalAlert({ open: true, severity, message });
      alertTimerRef.current = setTimeout(() => {
        setLocalAlert((a) => ({ ...a, open: false }));
        alertTimerRef.current = null;
      }, duration);
    },
    []
  );
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(
    () => () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    },
    []
  );
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback(
    (m) => showLocal(m, "error", 5000),
    [showLocal]
  );

  // ── Mutations ───────────────────────────────────────────────────
  const [listTreatmentsByPatient, { isLoading: isLoadingTreatments }] =
    useListTreatmentsByPatientMutation();
  const [deleteTreatment] = useDeleteTreatmentMutation();

  // ── Data state ──────────────────────────────────────────────────
  const [treatments, setTreatments] = useState([]);
  // Tanı / şikayet / ilaç / besin seçenekleri editörün seçim drawer'larında
  // sunucudan sayfalı aranır; burada toplu yüklenmez.
  const [initialLoaded, setInitialLoaded] = useState(false);

  // ── UI state ────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const examinationId = initialData?.examinationId;

  // ── Initial hydrate ─────────────────────────────────────────────
  useEffect(() => {
    if (mode === "session") {
      setTreatments(
        Array.isArray(initialData?.treatments) ? initialData.treatments : []
      );
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    initialData?.treatments,
  ]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      // Tanı / şikayet / ilaç / besin seçenekleri editörün seçim drawer'larında
      // sunucudan sayfalı aranır; burada yalnız tedaviler yüklenir.
      const treatmentsRes = await listTreatmentsByPatient({ token, patientId }).unwrap();
      setTreatments(treatmentsRes?.data?.treatments || []);
    } catch (_) {
      showError("Tedaviler yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [
    mode,
    token,
    patientId,
    listTreatmentsByPatient,
    showError,
  ]);

  useEffect(() => {
    if (mode === "standalone") {
      refresh();
    }
  }, [mode, refresh]);

  // ── Helpers ─────────────────────────────────────────────────────
  const sortedTreatments = useMemo(() => {
    const list = [...(treatments || [])];
    list.sort((a, b) => {
      // Active first, then by createdInfo desc
      const aActive = a.isActive ? 0 : 1;
      const bActive = b.isActive ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      const at = a.createdInfo ? new Date(a.createdInfo).getTime() : 0;
      const bt = b.createdInfo ? new Date(b.createdInfo).getTime() : 0;
      return bt - at;
    });
    return list;
  }, [treatments]);

  // ── İlk seçim / restore ─────────────────────────────────────────
  // Açılışta: session'da önce localStorage'daki açık kaydı geri yükle; yoksa
  // AKTİF tedavi varsa onu (en güncel aktif) seç. Aktif yoksa session'da en
  // güncel tedaviyi seç (boş ekran olmasın), standalone'da seçim yapma.
  useEffect(() => {
    if (!initialLoaded || sortedTreatments.length === 0) return;

    if (initialSelectedId && sortedTreatments.some((r) => r._id === initialSelectedId)) {
      setSelectedId(initialSelectedId);
      return;
    }

    if (mode === "session" && examinationId) {
      const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
      if (restored && sortedTreatments.some((r) => r._id === restored)) {
        setSelectedId(restored);
        return;
      }
    }

    // sortedTreatments aktifleri başa, sonra createdInfo desc sıraladığı için
    // ilk aktif = en güncel aktif tedavi.
    const activeRec = sortedTreatments.find((r) => r.isActive);
    if (activeRec) {
      setSelectedId(activeRec._id);
    } else if (mode === "session") {
      setSelectedId(sortedTreatments[0]?._id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, initialLoaded]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  // ── Editor callbacks ────────────────────────────────────────────
  const selected = useMemo(
    () => sortedTreatments.find((r) => r._id === selectedId) || null,
    [sortedTreatments, selectedId]
  );

  const handleSelect = (id) => {
    setCreatingNew(false);
    setSelectedId(id);
  };

  // Yeni tedaviye geçmeden önceki seçim — vazgeçilince geri dönülür
  const prevSelectedRef = useRef(null);

  const handleNew = () => {
    if (!canEdit) return;
    prevSelectedRef.current = selectedId;
    setSelectedId(null);
    setCreatingNew(true);
  };

  // Kaydetmeden yeni tedavi akışından çık
  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(prevSelectedRef.current);
  };

  const handleCreated = (created) => {
    if (!created || !created._id) return;
    setTreatments((prev) => [created, ...prev]);
    setSelectedId(created._id);
    setCreatingNew(false);
    showSuccess("Tedavi oluşturuldu.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setTreatments((prev) =>
      prev.map((r) => (r._id === updated._id ? updated : r))
    );
    showSuccess("Tedavi güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleEditorDelete = (rec) => {
    if (!rec?._id) return;
    setPendingDelete(rec);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      const promise = deleteTreatment({
        token,
        patientId,
        treatmentId: pendingDelete._id,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      setTreatments((prev) =>
        prev.filter((r) => r._id !== pendingDelete._id)
      );
      if (selectedId === pendingDelete._id) setSelectedId(null);
      showSuccess("Tedavi silindi.");
      if (typeof onRecordChanged === "function") onRecordChanged();
    } catch (_) {
      showError("Tedavi silinemedi.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleSetActive = (rec) => {
    // Local list'te isActive flag'lerini topluca güncelle (UI tutarlılığı).
    // Sunucu (setActiveTreatment) ile aynı: hedef aktif + status "active",
    // diğerlerinin yalnız isActive'i düşer.
    setTreatments((prev) =>
      prev.map((r) =>
        r._id === rec._id
          ? { ...r, isActive: true, status: "active" }
          : { ...r, isActive: false }
      )
    );
    showSuccess("Aktif tedavi güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleSetStatus = (rec, status) => {
    // Sunucu (setTreatmentStatus) ile aynı: "active" dışındaki durumlar
    // isActive'i düşürür; yalnız status güncellenirse seçici isActive'e
    // bakıp eski "Aktif"i göstermeye devam ediyordu.
    setTreatments((prev) =>
      prev.map((r) => {
        if (status === "active") {
          return r._id === rec._id
            ? { ...r, status, isActive: true }
            : { ...r, isActive: false };
        }
        return r._id === rec._id ? { ...r, status, isActive: false } : r;
      })
    );
    showSuccess("Tedavi durumu güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      // Sunucunun döndürdüğü spesifik sebebi (eksik/yanlış alan) göster.
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(
          serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`
        );
        return;
      }
      showError(serverMsg || "Tedavi kaydedilemedi.");
    },
    [showError, terms]
  );

  // ── Render ──────────────────────────────────────────────────────
  const showLoading =
    mode === "standalone" && !initialLoaded && isLoadingTreatments;
  const showEditor = creatingNew || Boolean(selected);

  // Yeni tedavi oluşturulurken butonu gizle — aktif oluşturma akışı varken
  // tekrar "Yeni Tedavi Oluştur"a basılmasını engelle.
  const headerAction =
    canEdit && !creatingNew ? (
      <ActionButton
        variant="add"
        compact
        palette={palette}
        onClick={handleNew}
        label="Yeni Tedavi Oluştur"
        aria-label="Yeni tedavi oluştur"
      />
    ) : null;

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Tedavi Planları"
        count={sortedTreatments.length}
        action={headerAction}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : sortedTreatments.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<MedicalServicesIcon />}
            title="Henüz tedavi planı yok"
            lead={`${terms.patientGen} ilk tedavi planını`}
            tail="Tanı, şikayet, reçete ve diyet tek planda toplanır."
            readOnlyText={`Bu ${terms.patientLower} için oluşturulmuş tedavi planı bulunmuyor.`}
            onCreate={canEdit ? handleNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {creatingNew && (
                  <CreatingRow
                    label="Yeni tedavi oluşturuluyor…"
                    hint="Tanı ve şikayet eklenince kaydedilir"
                    onCancel={handleCancelNew}
                    palette={palette}
                  />
                )}
                    {sortedTreatments.map((tr) => {
                      const active = tr._id === selectedId;
                      const diagnosesList = (tr.diagnoses || [])
                        .map(
                          (d) =>
                            d.diagnosisName ||
                            (d.diagnosisDisease && d.diagnosisDisease.name) ||
                            ""
                        )
                        .filter(Boolean);
                      const medCount = (tr.medications || []).length;
                      const allowedCount = (tr.allowedFoods || []).length;
                      const forbiddenCount = (tr.forbiddenFoods || []).length;
                      const foodTotal = allowedCount + forbiddenCount;
                      const nextDate = tr.nextControlDate
                        ? formatDate(tr.nextControlDate)
                        : null;
                      const createdStr = tr.createdInfo
                        ? formatDate(tr.createdInfo)
                        : "—";
                      const statusStyle = getStatusStyle(
                        tr.status,
                        tr.isActive,
                        palette
                      );

                      return (
                        <SelectableListRow
                          key={tr._id}
                          active={active}
                          onClick={() => handleSelect(tr._id)}
                          sx={LIST_ROW_SX}
                          title={
                            diagnosesList.length
                              ? diagnosesList.join(", ")
                              : "Tanı belirtilmemiş"
                          }
                          subtitle={
                            <RowLine flex sx={{ mt: 0.5 }}>
                              <Box
                                component="span"
                                sx={{ fontSize: "0.8rem", color: COLORS.slate700, fontWeight: 700 }}
                              >
                                {createdStr}
                              </Box>
                              {medCount > 0 && (
                                <RowChip
                                  icon={<LocalPharmacyIcon sx={{ fontSize: "13px !important" }} />}
                                  label={`${medCount} ilaç`}
                                  sx={{
                                    height: 22,
                                    bgcolor: alpha(palette.primary, 0.08),
                                    color: palette.primary,
                                    "& .MuiChip-icon": { color: palette.primary },
                                  }}
                                />
                              )}
                              {foodTotal > 0 && (
                                <RowChip
                                  icon={<RestaurantIcon sx={{ fontSize: "13px !important" }} />}
                                  label={`${foodTotal} besin`}
                                  sx={{
                                    height: 22,
                                    bgcolor: COLORS.warningLight,
                                    color: COLORS.warningDarker,
                                    "& .MuiChip-icon": { color: COLORS.warning },
                                  }}
                                />
                              )}
                              {nextDate && (
                                <RowChip
                                  icon={<EventNoteIcon sx={{ fontSize: "13px !important" }} />}
                                  label={nextDate}
                                  sx={{
                                    height: 22,
                                    bgcolor: COLORS.successLight,
                                    color: COLORS.successDarker,
                                    "& .MuiChip-icon": { color: COLORS.successDark },
                                  }}
                                />
                              )}
                            </RowLine>
                          }
                          trailing={
                            <>
                              <Chip
                                label={getStatusLabel(tr.status, tr.isActive)}
                                size="small"
                                icon={
                                  tr.isActive ? (
                                    <MedicalServicesIcon
                                      sx={{ fontSize: "13px !important" }}
                                    />
                                  ) : undefined
                                }
                                sx={{
                                  height: 24,
                                  fontSize: "0.7rem",
                                  borderRadius: "999px",
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  ...statusStyle,
                                }}
                              />
                              {active && canEdit && (
                                <Tooltip title="Tedaviyi sil">
                                  <IconButton
                                    size="small"
                                    aria-label="Tedaviyi sil"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditorDelete(tr);
                                    }}
                                    sx={{
                                      flexShrink: 0,
                                      color: COLORS.slate400,
                                      "&:hover": {
                                        color: COLORS.errorDark,
                                        bgcolor: alpha(COLORS.error, 0.08),
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          }
                        />
                      );
                    })}
              </Stack>
            }
            editor={
              !showEditor ? (
                <SelectPrompt
                  icon={<MedicalServicesIcon />}
                  title="Bir tedavi planı seçin"
                  description="Detayları görmek ve düzenlemek için soldaki listeden bir plan seçin."
                />
              ) : (
                <TreatmentEditor
                  key={selected?._id || "new"}
                  record={selected}
                  mode={mode}
                  patient={patient}
                  patientId={patientId}
                  token={token}
                  examinationId={examinationId}
                  canEdit={canEdit}
                  onCreated={handleCreated}
                  onSaved={handleSaved}
                  onSetActive={handleSetActive}
                  onSetStatus={handleSetStatus}
                  onError={handleEditorError}
                  registerAutoSave={registerAutoSave}
                  registerFlush={registerFlush}
                  onSaveStatusChange={onSaveStatusChange}
                />
              )
            }
          />
        )}
      </WorkspaceBody>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Tedavi planını sil"
        message="Bu tedavi planı kalıcı olarak silinecek. Devam edilsin mi?"
        confirmText="Sil"
        cancelText="Vazgeç"
        loading={deleteLoading}
      />
    </Box>
  );
}
